import { DroppableWorkspace } from "@/components/workspace/DroppableWorkspace";
import { SortableThreadItem } from "@/components/workspace/SortableThreadItem";
import { ThreadItem } from "@/components/workspace/ThreadItem";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { useToast } from "@/hooks/useToast";
import { SupabaseThreadUtils } from "@/lib/db/utils/supabase/threads";
import { SupabaseWorkspaceUtils } from "@/lib/db/utils/supabase/workspace";
import { supabase } from "@/lib/supabase";
import { supabaseAdmin } from "@/lib/supabaseAdmin";
import { isWorkspaceMember } from "@/lib/utils/teamAccessUtils";
import { setThreadShared } from "@/utils/shareUtils";
import { refreshThreadsAndCleanup } from "@/lib/utils/threadCleanupUtils";
import useAuthStore from "@/store/authStore";
import { useThreadStore } from "@/store/threadStore";
import { useWorkspaceThreadsStore } from "@/store/workspaceThreadsStore";
import PropTypes from "prop-types";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

// Stable empty array to avoid infinite loops from Zustand selector
const EMPTY_THREADS = [];

export function WorkspaceChats({
  workspace,
  threadDetails: propThreadDetails,
  loadingThreads: propLoadingThreads,
  onCreateChat,
  creatingChat,
  onRefreshThreads,
}) {
  const navigate = useNavigate();
  // Add state for managing our own thread loading
  const [internalThreadDetails, setInternalThreadDetails] = useState({});
  const [internalLoading, setInternalLoading] = useState(true);
  const [threadFirstMessages, setThreadFirstMessages] = useState({});
  const [isRenaming, setIsRenaming] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [selectedThread, setSelectedThread] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [deletingThreadIds, setDeletingThreadIds] = useState(new Set());
  const [optimisticWorkspaceThreads, setOptimisticWorkspaceThreads] =
    useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const { user, workspaces, setWorkspaces } = useAuthStore();
  const { setMessages, setCurrentThreadContext, clearCurrentThread } =
    useThreadStore();
  // Subscribe to workspace threads store for reordering support
  // Get the entire store state to ensure re-renders on any thread changes
  const workspaceThreadsFromStore = useWorkspaceThreadsStore(
    (state) => state.workspaceThreads
  );
  // Local state to force re-render when threads are reordered via custom event
  const [reorderedThreads, setReorderedThreads] = useState(null);
  // Extract the specific workspace's threads, preferring local reordered state
  const storeThreads = reorderedThreads ||
    (workspace?.id && workspaceThreadsFromStore[workspace.id]) || EMPTY_THREADS;
  const inputRef = useRef(null);

  // Listen for thread reorder events to update UI immediately
  useEffect(() => {
    const handleThreadsReordered = (event) => {
      const { workspaceId, threads } = event.detail;
      if (workspaceId === workspace?.id) {
        setReorderedThreads(threads);
      }
    };

    window.addEventListener("workspace-threads-reordered", handleThreadsReordered);
    return () => {
      window.removeEventListener("workspace-threads-reordered", handleThreadsReordered);
    };
  }, [workspace?.id]);

  // Clear reordered threads when workspace changes
  useEffect(() => {
    setReorderedThreads(null);
  }, [workspace?.id]);

  // Track theme for empty state styling
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Add drag drop animation styles
  useEffect(() => {
    import("@/components/Chat/input/constants/styleConstants.js").then(
      ({ dragDropAnimationStyle }) => {
        const existingStyle = document.getElementById("drag-drop-animations");
        if (!existingStyle) {
          const animationStyleElement = document.createElement("style");
          animationStyleElement.id = "drag-drop-animations";
          animationStyleElement.textContent = dragDropAnimationStyle;
          document.head.appendChild(animationStyleElement);
        }
      }
    );
  }, []);

  // Add ref to track subscription to prevent duplicates
  const subscriptionRef = useRef(null);
  const isMountedRef = useRef(true);
  const deletionInProgressRef = useRef(false);
  const lastWorkspaceThreadsRef = useRef(null);

  const { toast } = useToast();

  // Cleanup subscription on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (subscriptionRef.current) {
        supabase.removeChannel(subscriptionRef.current);
        subscriptionRef.current = null;
      }
    };
  }, []);

  // Reset optimistic workspace threads when workspace changes
  useEffect(() => {
    setOptimisticWorkspaceThreads(null);
  }, [workspace?.id]);

  // Add function to create chat directly with Supabase without OpenAI
  const handleCreateWorkspaceChat = (workspaceId) => {
    // Clear entire thread state including messages, context, etc.
    clearCurrentThread();

    // Store workspace context in localStorage to ensure it's available after navigation
    localStorage.setItem("pendingWorkspaceChat", workspaceId);

    // Add a flag to explicitly indicate that this should be a Supabase-only thread
    localStorage.setItem("createSupabaseOnlyThread", "true");

    // Set a flag to force a fresh thread creation without reusing any existing thread
    localStorage.setItem("forceNewThread", "true");

    // Add a flag to clear messages and thread context
    localStorage.setItem("clearMessagesAndContext", "true");

    // Instead, add a flag to indicate this needs title generation
    localStorage.setItem("needs_title_generation", "true");

    // Call the parent component's onCreateChat function
    onCreateChat();
  };

  // Check for dark mode on mount and when theme changes
  useEffect(() => {
    // Function to check if dark mode is active
    const checkDarkMode = () => {
      const isDark = document.documentElement.classList.contains("dark");
      setIsDarkMode(isDark);
    };

    // Initial check
    checkDarkMode();

    // Set up observer to detect class changes on html element
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === "class") {
          checkDarkMode();
        }
      });
    });

    observer.observe(document.documentElement, { attributes: true });

    return () => observer.disconnect();
  }, []);

  // After setting up dark mode detection
  // Add useEffect to handle navigation after thread creation
  useEffect(() => {
    // Function to check for newly created threads
    const checkForNewThread = () => {
      const newThreadId = localStorage.getItem("newlyCreatedThreadId");
      if (newThreadId) {
        // Clear the flag
        localStorage.removeItem("newlyCreatedThreadId");

        // Navigate to the new thread
        navigate(`/chat/${newThreadId}`);
      }
    };

    // Check when component mounts
    checkForNewThread();

    // Create a small interval to check for the thread creation
    // This is more reliable than the storage event which might not fire in the same window
    const intervalId = setInterval(checkForNewThread, 500);

    return () => {
      clearInterval(intervalId);
    };
  }, [navigate]);

  // Use either prop thread details or internal thread details
  const threadDetails =
    Object.keys(propThreadDetails).length > 0
      ? propThreadDetails
      : internalThreadDetails;

  const loadingThreads = propLoadingThreads || internalLoading;

  // Function to fetch first message of a thread
  const fetchFirstMessage = async (threadId, isMember, isTeam) => {
    try {
      // Safety check: ensure threadId is valid
      if (!threadId) {
        console.warn("Cannot fetch first message: threadId is undefined");
        return null;
      }

      const dbClient = isMember && isTeam ? supabaseAdmin : supabase;

      const { data: messages, error } = await dbClient
        .from("thread_messages")
        .select("content, role, created_at")
        .eq("thread_id", threadId)
        .order("created_at", { ascending: true })
        .limit(1);

      if (error) {
        console.error("Error fetching first message:", error);
        return null;
      }

      if (messages && messages.length > 0) {
        const firstMessage = messages[0];
        // Extract text content from the message
        let content = "";

        if (typeof firstMessage.content === "string") {
          content = firstMessage.content;
        } else if (Array.isArray(firstMessage.content)) {
          content = firstMessage.content.reduce((acc, item) => {
            if (item.type === "text") {
              if (typeof item.text === "string") return acc + item.text;
              if (item.text?.value) return acc + item.text.value;
            }
            return acc;
          }, "");
        } else if (typeof firstMessage.content === "object") {
          content =
            firstMessage.content?.text?.value ||
            firstMessage.content?.text ||
            JSON.stringify(firstMessage.content);
        }

        // Truncate content to a reasonable length for subtitle
        const truncatedContent =
          content && content.length > 100
            ? content.substring(0, 100) + "..."
            : content || "";

        return {
          content: truncatedContent,
          role: firstMessage.role,
          created_at: firstMessage.created_at,
        };
      }

      return null;
    } catch (error) {
      console.error(
        "Error fetching first message for thread:",
        threadId,
        error
      );
      return null;
    }
  };

  // Fetch thread details for all workspace threads
  useEffect(() => {
    if (
      !workspace?.id ||
      !workspace.workspace_threads ||
      Object.keys(propThreadDetails).length > 0
    ) {
      setInternalLoading(false);
      return;
    }

    // Don't refetch if we have optimistic updates in progress or deletion in progress
    if (optimisticWorkspaceThreads !== null || deletionInProgressRef.current) {
      setInternalLoading(false);
      return;
    }

    // Check if workspace threads actually changed (not just a re-render)
    const currentThreadsString = JSON.stringify(
      workspace.workspace_threads?.sort()
    );
    const lastThreadsString = lastWorkspaceThreadsRef.current;

    if (lastThreadsString === currentThreadsString) {
      setInternalLoading(false);
      return;
    }

    // Update the ref with current threads
    lastWorkspaceThreadsRef.current = currentThreadsString;

    const fetchThreadDetails = async () => {
      setInternalLoading(true);
      try {
        if (workspace.workspace_threads.length === 0) {
          setInternalThreadDetails({});
          setThreadFirstMessages({});
          setInternalLoading(false);
          return;
        }

        // Check if user is a workspace member to determine access approach
        const isMember = await isWorkspaceMember(user.id, workspace.id);

        let threadsData = [];
        let staleThreadIds = [];

        if (isMember && workspace.is_team) {
          // If this is a team workspace and user is a member, use admin client
          // Fetch thread details using admin client to bypass RLS
          const { data, error } = await supabaseAdmin
            .from("threads")
            .select(
              "id, title, updated_at, metadata, openai_thread_id, status, user_id, is_team, is_shared, workspace_id"
            )
            .in("id", workspace.workspace_threads)
            .order("updated_at", { ascending: true });

          if (error) throw error;
          threadsData = data || [];

          // Mark threads as shared
          threadsData = threadsData.map((thread) => ({
            ...thread,
            metadata: {
              ...(thread.metadata || {}),
              is_shared_thread: true,
              shared_in_workspace: workspace.id,
            },
          }));
        } else {
          // For personal workspaces or if user is not a member, use regular client
          const { data, error } = await supabase
            .from("threads")
            .select(
              "id, title, updated_at, metadata, openai_thread_id, status, is_team, is_shared, workspace_id"
            )
            .in("id", workspace.workspace_threads)
            .order("updated_at", { ascending: true });

          if (error) throw error;
          threadsData = data || [];
        }

        // Find stale thread IDs (threads that are in workspace but don't exist in database)
        const existingThreadIds = threadsData.map((thread) => thread.id);
        staleThreadIds = workspace.workspace_threads.filter(
          (threadId) => !existingThreadIds.includes(threadId)
        );

        // If we found stale thread IDs, clean them up from the workspace
        if (staleThreadIds.length > 0) {
          try {
            // Remove stale thread IDs from workspace
            const updatedWorkspaceThreads = workspace.workspace_threads.filter(
              (threadId) => !staleThreadIds.includes(threadId)
            );

            // Update the workspace in the database
            const { error: updateError } = await supabase
              .from("workspaces")
              .update({
                workspace_threads: updatedWorkspaceThreads,
                updated_at: new Date().toISOString(),
              })
              .eq("id", workspace.id);

            if (updateError) {
              console.warn(
                "Failed to clean up stale thread references:",
                updateError
              );
            } else {
              // Update the local workspace state
              const { workspaces, setWorkspaces } = useAuthStore.getState();
              const updatedWorkspaces = workspaces.map((ws) => {
                if (ws.id === workspace.id) {
                  return {
                    ...ws,
                    workspace_threads: updatedWorkspaceThreads,
                  };
                }
                return ws;
              });
              setWorkspaces(updatedWorkspaces);

              // Trigger refresh callback if provided
              if (onRefreshThreads) {
                onRefreshThreads();
              }
            }
          } catch (cleanupError) {
            console.error(
              "Error cleaning up stale thread references:",
              cleanupError
            );
          }
        }

        // Only process threads that actually exist in the database
        const formattedThreads = threadsData.reduce((acc, thread) => {
          // Safety check: ensure thread has a valid id
          if (!thread || !thread.id) {
            console.warn("Skipping thread with missing id:", thread);
            return acc;
          }

          // Skip threads with default/untitled titles unless they have actual content
          const isDefaultTitle =
            !thread.title ||
            thread.title === "New Chat" ||
            thread.title === "Untitled Chat" ||
            (thread.title.startsWith("Chat ") &&
              thread.title.match(/^Chat [a-f0-9]{6}\.\.\.$/i));

          if (isDefaultTitle) {
            return acc;
          }

          acc[thread.id] = {
            id: thread.id,
            title:
              thread.title ||
              `Chat ${thread.id?.substring(0, 6) || "Unknown"}...`,
            lastMessageAt: thread.updated_at,
            metadata: {
              ...(thread.metadata || {}),
              is_shared_thread:
                thread.metadata?.is_shared_thread ||
                thread.is_team === true ||
                thread.is_shared === true ||
                workspace.is_team,
              shared_in_workspace:
                thread.metadata?.shared_in_workspace ||
                (thread.is_team === true ? workspace.id : null) ||
                workspace.id,
              created_by: thread.user_id || thread.metadata?.created_by,
            },
            openai_thread_id: thread.openai_thread_id,
            status: thread.status || "active",
            user_id: thread.user_id,
            is_team: thread.is_team,
            is_shared: thread.is_shared,
            workspace_id: thread.workspace_id,
          };
          return acc;
        }, {});

        setInternalThreadDetails(formattedThreads);

        // Fetch first messages for valid threads only
        const firstMessages = {};
        const validThreads = threadsData.filter(
          (thread) => thread && thread.id && formattedThreads[thread.id]
        );

        await Promise.all(
          validThreads.map(async (thread) => {
            const firstMessage = await fetchFirstMessage(
              thread.id,
              isMember,
              workspace.is_team
            );
            if (firstMessage) {
              firstMessages[thread.id] = firstMessage;
            }
          })
        );

        setThreadFirstMessages(firstMessages);
      } catch (error) {
        // Handle error silently
        console.error("Error fetching thread details:", error);
      } finally {
        setInternalLoading(false);
      }
    };

    fetchThreadDetails();
  }, [
    workspace?.id,
    workspace?.workspace_threads,
    propThreadDetails,
    user?.id,
    workspace?.is_team,
    navigate,
    optimisticWorkspaceThreads, // Include this so the effect knows about optimistic state
    refreshTrigger, // Include this to trigger refresh after successful drops
    onRefreshThreads, // Include this to trigger refresh callback
  ]);

  // Fetch first messages for threads that don't have them yet
  // This handles the case when threads come from propThreadDetails (e.g., after drag-drop)
  useEffect(() => {
    if (!workspace?.id || !user?.id) return;

    // Get the thread details we're actually using
    const activeThreadDetails =
      Object.keys(propThreadDetails).length > 0
        ? propThreadDetails
        : internalThreadDetails;

    // Find threads that don't have first messages yet
    const threadsWithoutFirstMessage = Object.keys(activeThreadDetails).filter(
      (threadId) => !threadFirstMessages[threadId]
    );

    if (threadsWithoutFirstMessage.length === 0) return;

    const fetchMissingFirstMessages = async () => {
      // Check if user is a workspace member
      const isMember = await isWorkspaceMember(user.id, workspace.id);
      const isTeam = workspace.is_team;

      const newFirstMessages = {};

      await Promise.all(
        threadsWithoutFirstMessage.map(async (threadId) => {
          const firstMessage = await fetchFirstMessage(threadId, isMember, isTeam);
          if (firstMessage) {
            newFirstMessages[threadId] = firstMessage;
          }
        })
      );

      if (Object.keys(newFirstMessages).length > 0) {
        setThreadFirstMessages((prev) => ({
          ...prev,
          ...newFirstMessages,
        }));
      }
    };

    fetchMissingFirstMessages();
  }, [propThreadDetails, internalThreadDetails, workspace?.id, workspace?.is_team, user?.id]);

  // Add a real-time subscription for thread updates
  useEffect(() => {
    if (
      !workspace?.id ||
      !workspace?.workspace_threads?.length ||
      !isMountedRef.current
    )
      return;

    // Clean up existing subscription first
    if (subscriptionRef.current) {
      supabase.removeChannel(subscriptionRef.current);
      subscriptionRef.current = null;
    }

    // Create a unique channel name to avoid conflicts
    const channelName = `workspace-threads-${workspace.id}-${Date.now()}`;

    // Subscribe to thread changes
    const threadChannel = supabase
      .channel(channelName)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "threads",
          filter: `id=in.(${workspace.workspace_threads.join(",")})`,
        },
        (payload) => {
          // Only update if component is still mounted
          if (!isMountedRef.current) return;

          // Don't process updates for threads that are being deleted optimistically
          if (
            payload.new &&
            payload.new.id &&
            deletingThreadIds.has(payload.new.id)
          ) {
            return;
          }

          // Update thread details when a thread changes
          if (payload.new && payload.new.id) {
            setInternalThreadDetails((prev) => ({
              ...prev,
              [payload.new.id]: {
                id: payload.new.id,
                title:
                  payload.new.title ||
                  `Chat ${payload.new.id?.substring(0, 6) || "Unknown"}...`,
                lastMessageAt: payload.new.updated_at,
                metadata: {
                  ...(payload.new.metadata || {}),
                  is_shared_thread:
                    payload.new.metadata?.is_shared_thread ||
                    payload.new.is_team === true ||
                    payload.new.is_shared === true ||
                    workspace.is_team,
                  shared_in_workspace:
                    payload.new.metadata?.shared_in_workspace ||
                    (payload.new.is_team === true ? workspace.id : null) ||
                    workspace.id,
                },
                openai_thread_id: payload.new.openai_thread_id,
                status: payload.new.status || "active",
                is_team: payload.new.is_team,
                is_shared: payload.new.is_shared,
                workspace_id: payload.new.workspace_id,
              },
            }));
          }
        }
      )
      .subscribe();

    // Store the subscription reference
    subscriptionRef.current = threadChannel;

    return () => {
      // Properly cleanup the subscription
      if (subscriptionRef.current) {
        supabase.removeChannel(subscriptionRef.current);
        subscriptionRef.current = null;
      }
    };
  }, [workspace?.id]); // Remove workspace_threads from dependencies to prevent re-subscription

  // Thread action handlers (similar to NavChatHistory)
  const handleThreadDelete = async (thread) => {
    // Safety check: ensure thread and thread.id exist
    if (!thread || !thread.id) {
      console.error("Cannot delete thread: thread or thread.id is undefined");
      return;
    }

    // OPTIMISTIC UI: Immediately close dropdown and update UI
    setOpenDropdownId(null);

    // Add thread to deleting state for visual feedback
    setDeletingThreadIds((prev) => new Set([...prev, thread.id]));

    // Set deletion in progress flag
    deletionInProgressRef.current = true;

    // Store original state for potential rollback
    const originalThreadDetails = { ...internalThreadDetails };
    const originalFirstMessages = { ...threadFirstMessages };
    const originalThreadStore = useThreadStore.getState();
    const originalWorkspaces = [...workspaces];

    // Check if this is the current thread for navigation
    const isCurrentThread = originalThreadStore.currentThread?.id === thread.id;

    try {
      // OPTIMISTIC UI: Immediately update local state to remove the thread
      setInternalThreadDetails((prev) => {
        const updated = { ...prev };
        delete updated[thread.id];
        return updated;
      });

      setThreadFirstMessages((prev) => {
        const updated = { ...prev };
        delete updated[thread.id];
        return updated;
      });

      // OPTIMISTIC UI: Immediately update thread store
      useThreadStore.setState((state) => ({
        threads: state.threads.filter((t) => t.id !== thread.id),
        // Clear current thread if it's the one being deleted
        currentThread: isCurrentThread ? null : state.currentThread,
        currentThreadId: isCurrentThread ? null : state.currentThreadId,
        currentThreadTitle: isCurrentThread ? null : state.currentThreadTitle,
        // Clear messages if deleting current thread
        messages: isCurrentThread ? [] : state.messages,
      }));

      // OPTIMISTIC UI: Immediately update local workspace threads for this component
      if (workspace?.workspace_threads?.includes(thread.id)) {
        setOptimisticWorkspaceThreads(
          workspace.workspace_threads.filter((id) => id !== thread.id)
        );
      }

      // OPTIMISTIC UI: Update global workspace state
      const updatedWorkspaces = workspaces.map((ws) => {
        if (ws.workspace_threads && ws.workspace_threads.includes(thread.id)) {
          return {
            ...ws,
            workspace_threads: ws.workspace_threads.filter(
              (id) => id !== thread.id
            ),
          };
        }
        return ws;
      });
      setWorkspaces(updatedWorkspaces);

      // OPTIMISTIC UI: Navigate immediately if deleting current thread
      if (isCurrentThread) {
        navigate("/chat/new");
      }

      // Now perform the actual deletion in the background
      setIsLoading(true);

      // 1. Delete the thread from Supabase
      const { success, error } = await SupabaseThreadUtils.deleteThread({
        supabaseThreadId: thread.id,
      });

      if (error) {
        throw error;
      }

      // 2. Remove thread from workspaces in the database
      await Promise.all(
        originalWorkspaces.map(async (ws) => {
          if (
            ws.workspace_threads &&
            ws.workspace_threads.includes(thread.id)
          ) {
            try {
              await SupabaseWorkspaceUtils.removeThreadFromWorkspace(
                ws.id,
                thread.id
              );
            } catch (workspaceError) {
              console.error("Error updating workspace:", workspaceError);
              // Note: We don't rollback here as the thread is already deleted
            }
          }
        })
      );

      // Clear optimistic state since the deletion was successful
      setOptimisticWorkspaceThreads(null);

      // Refresh threads list and cleanup any untitled chats
      try {
        await refreshThreadsAndCleanup({
          removeUntitledChats: true,
          onProgress: (progress) => {},
        });

        // Trigger refresh callback if provided
        if (onRefreshThreads) {
          onRefreshThreads();
        }
      } catch (refreshError) {
        console.warn("Error during thread refresh and cleanup:", refreshError);
      }
    } catch (error) {
      console.error("Error deleting thread:", error);

      // ROLLBACK: Restore original state on error
      setInternalThreadDetails(originalThreadDetails);
      setThreadFirstMessages(originalFirstMessages);

      // Restore thread store state
      useThreadStore.setState({
        threads: originalThreadStore.threads,
        currentThread: originalThreadStore.currentThread,
        currentThreadId: originalThreadStore.currentThreadId,
        currentThreadTitle: originalThreadStore.currentThreadTitle,
        messages: originalThreadStore.messages,
      });

      // Restore workspace state
      setWorkspaces(originalWorkspaces);

      // Restore optimistic workspace threads
      setOptimisticWorkspaceThreads(null);

      // Navigate back if we had navigated away
      if (isCurrentThread && originalThreadStore.currentThread) {
        navigate(`/chat/${originalThreadStore.currentThread.id}`);
      }

      // Show error feedback to user
      toast({
        title: "Error deleting chat",
        description: "Failed to delete the chat. Changes have been reverted.",
        variant: "destructive",
      });
    } finally {
      // Remove thread from deleting state
      setDeletingThreadIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(thread.id);
        return newSet;
      });

      // Clear deletion in progress flag
      deletionInProgressRef.current = false;
      setIsLoading(false);
    }
  };

  const startRenaming = (thread) => {
    // First close dropdown and reset any existing state
    setOpenDropdownId(null);

    // Use a small delay to ensure dropdown is fully closed
    setTimeout(() => {
      setSelectedThread(thread);
      setNewTitle(thread.title || "");
      setIsRenaming(true);
    }, 100);
  };

  // Effect to focus the input when renaming starts
  useEffect(() => {
    if (isRenaming && inputRef.current) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        inputRef.current.focus();
        // Select all text to make editing easier
        inputRef.current.select();
      }, 150);
    }
  }, [isRenaming]);

  const handleRename = async () => {
    if (!selectedThread || !newTitle.trim()) return;

    try {
      setIsLoading(true);

      // Update thread title in Supabase directly
      const { data, error } = await supabase
        .from("threads")
        .update({
          title: newTitle.trim(),
          updated_at: new Date().toISOString(),
        })
        .eq("id", selectedThread.id)
        .select("*")
        .single();

      if (error) {
        console.error("Error updating thread title in Supabase:", error);
        throw error;
      }

      // Update local state with the updated thread
      useThreadStore.setState((state) => {
        const threadIsCurrentThread =
          state.currentThread?.id === selectedThread.id;

        return {
          threads: state.threads.map((t) =>
            t.id === selectedThread.id
              ? {
                  ...t,
                  title: newTitle.trim(),
                  updated_at: new Date().toISOString(),
                }
              : t
          ),
          currentThread: threadIsCurrentThread
            ? {
                ...state.currentThread,
                title: newTitle.trim(),
                updated_at: new Date().toISOString(),
              }
            : state.currentThread,
          currentThreadTitle: threadIsCurrentThread
            ? newTitle.trim()
            : state.currentThreadTitle,
        };
      });

      // Update internal thread details
      setInternalThreadDetails((prev) => ({
        ...prev,
        [selectedThread.id]: {
          ...prev[selectedThread.id],
          title: newTitle.trim(),
        },
      }));

      setIsRenaming(false);
      setSelectedThread(null);
    } catch (error) {
      console.error("Error renaming thread:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelRename = () => {
    setIsRenaming(false);
    setSelectedThread(null);
    setNewTitle("");
  };

  const handleToggleBookmark = async (thread) => {
    try {
      // Close dropdown immediately for better UX
      setOpenDropdownId(null);
      setIsLoading(true);

      // Get current bookmark status
      const isBookmarked = thread.metadata?.bookmarked || false;

      // Update thread metadata with new bookmark status
      const updatedMetadata = {
        ...(thread.metadata || {}),
        bookmarked: !isBookmarked,
        bookmark_updated_at: new Date().toISOString(),
      };

      // Update in Supabase
      const { thread: updatedThread, error } =
        await SupabaseThreadUtils.updateThreadMetadata({
          supabaseThreadId: thread.id,
          metadata: updatedMetadata,
        });

      if (error) {
        console.error("Error toggling bookmark:", error);
        throw error;
      }

      // Update threads state
      useThreadStore.setState((state) => {
        const threadIsCurrentThread = state.currentThread?.id === thread.id;

        return {
          threads: state.threads.map((t) =>
            t.id === thread.id ? { ...t, metadata: updatedMetadata } : t
          ),
          currentThread: threadIsCurrentThread
            ? { ...state.currentThread, metadata: updatedMetadata }
            : state.currentThread,
        };
      });

      // Update local state
      setInternalThreadDetails((prev) => ({
        ...prev,
        [thread.id]: {
          ...prev[thread.id],
          metadata: updatedMetadata,
        },
      }));
    } catch (error) {
      console.error("Error toggling bookmark:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleShare = async (thread) => {
    try {
      // Close dropdown immediately for better UX
      setOpenDropdownId(null);
      setIsLoading(true);

      // Set the thread as shared and get the share URL
      const { success, error, shareUrl } = await setThreadShared(thread.id, true);

      if (error || !success) {
        throw error || new Error("Failed to share thread");
      }

      // Copy the share URL to clipboard
      await navigator.clipboard.writeText(shareUrl);

      toast({
        title: "Link copied",
        description: "Share link has been copied to clipboard",
      });
    } catch (error) {
      console.error("Error sharing thread:", error);
      toast({
        title: "Error sharing chat",
        description: "Failed to create share link. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleThreadClone = async (thread) => {
    try {
      // Close dropdown immediately for better UX
      setOpenDropdownId(null);
      setIsLoading(true);

      // Get the current user and workspace info from auth store
      const { user, currentWorkspaceId } = useAuthStore.getState();

      // 1. Get original thread data from Supabase
      const { data: originalThread, error: threadError } = await supabase
        .from("threads")
        .select("*")
        .eq("id", thread.id)
        .single();

      if (threadError) {
        throw new Error(`Failed to fetch thread: ${threadError.message}`);
      }

      // Use the workspace_id from the original thread, or default to the current workspace
      const workspaceId = originalThread.workspace_id || currentWorkspaceId;

      if (!workspaceId) {
        throw new Error(
          "No workspace ID available. Please select a workspace first."
        );
      }

      // 2. Get messages from the original thread
      const { data: originalMessages, error: messagesError } = await supabase
        .from("thread_messages")
        .select("*")
        .eq("thread_id", thread.id)
        .order("created_at", { ascending: true });

      if (messagesError) {
        throw new Error(`Failed to fetch messages: ${messagesError.message}`);
      }

      // 3. Create a new thread in Supabase
      const newThreadTitle = `Clone of ${thread.title || "Untitled Chat"}`;
      const timestamp = new Date().toISOString();

      // Generate a unique clone ID
      const cloneId = `clone-${Date.now()}-${Math.random().toString(36).substring(2, 10)}`;

      // Prepare metadata with original thread info
      const threadMetadata = {
        ...originalThread.metadata,
        cloned_from: thread.id,
        cloned_at: timestamp,
        original_title: thread.title,
        clone_id: cloneId,
        is_team: workspace.is_team === true,
        is_shared_thread: workspace.is_team === true,
        shared_in_workspace: workspace.is_team === true ? workspaceId : null,
      };

      // Insert new thread into Supabase
      const { data: newThread, error: newThreadError } = await supabase
        .from("threads")
        .insert({
          title: newThreadTitle,
          user_id: originalThread.user_id,
          workspace_id: workspaceId, // Ensure workspace_id is set
          metadata: threadMetadata,
          created_at: timestamp,
          updated_at: timestamp,
          provider: originalThread.provider || "openai",
          status: "active",
          is_team: workspace.is_team === true,
          is_shared: workspace.is_team === true,
        })
        .select("*")
        .single();

      if (newThreadError) {
        throw new Error(
          `Failed to create new thread: ${newThreadError.message}`
        );
      }

      // 4. Copy all messages to the new thread
      let insertedMessages = [];
      if (originalMessages && originalMessages.length > 0) {
        // Prepare messages for batch insert
        const messagesToInsert = originalMessages.map((message) => {
          // Generate a new random message ID for each message
          const newMessageId = `msg_${Date.now()}_${Math.random().toString(36).substring(2, 10)}`;

          return {
            thread_id: newThread.id,
            message_id: newMessageId,
            role: message.role,
            content: message.content,
            metadata: message.metadata || {},
            created_at: timestamp,
            workspace_id: workspaceId, // Explicitly set workspace_id
            user_id: originalThread.user_id,
            provider: message.provider || "openai",
            file_ids: message.file_ids || [],
            tools_count: message.tools_count || 0,
            attachments: message.attachments,
            annotations: message.annotations,
            tool_calls: message.tool_calls,
          };
        });

        // Insert all messages in batches of 100 to avoid potential size limits
        for (let i = 0; i < messagesToInsert.length; i += 100) {
          const batch = messagesToInsert.slice(i, i + 100);
          const { data: insertedBatch, error: messagesInsertError } =
            await supabase.from("thread_messages").insert(batch).select("*");

          if (messagesInsertError) {
            console.error(
              `Error inserting messages batch ${i / 100 + 1}:`,
              messagesInsertError
            );
          } else if (insertedBatch) {
            insertedMessages = [...insertedMessages, ...insertedBatch];
          }
        }

        // Update the thread's message count
        const { error: threadUpdateError } = await supabase
          .from("threads")
          .update({
            message_count: originalMessages.length,
            last_message_at: timestamp,
            last_activity: timestamp,
          })
          .eq("id", newThread.id);

        if (threadUpdateError) {
          console.error(
            "Error updating thread message count:",
            threadUpdateError
          );
        }
      }

      // 5. Add the new thread to the thread store
      // Sort the inserted messages by created_at before setting in state
      const chronologicallySortedMessages = insertedMessages.sort((a, b) => {
        const aDate = new Date(a.created_at).getTime();
        const bDate = new Date(b.created_at).getTime();
        return aDate - bDate; // Ascending order (oldest first)
      });

      useThreadStore.setState((state) => {
        return {
          threads: [newThread, ...state.threads],
          // Set messages in the thread store if we're switching to this thread
          messages: chronologicallySortedMessages,
        };
      });

      // 6. Navigate to the new thread
      navigate(`/chat/${newThread.id}`);
    } catch (error) {
      console.error("Error cloning thread:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLocalThreadSelect = useCallback(
    async (localThread) => {
      try {
        // Check if we're in an intentional project navigation
        const intentionalProjectNav = localStorage.getItem(
          "intentionalProjectNavigation"
        );
        if (intentionalProjectNav === "true") {
          // If we just navigated to a project intentionally, don't override with thread navigation

          localStorage.removeItem("intentionalProjectNavigation");
          return;
        }

        if (localThread?.id) {
          setIsLoading(true);

          // Use a more efficient and reliable approach for thread selection
          // Single state update with critical flags to ensure message loading
          useThreadStore.setState({
            currentThread: localThread,
            currentThreadId: localThread.id,
            currentThreadTitle: localThread.title || "Untitled Chat",
            threadSwitchInProgress: true, // Signal that we're switching threads
            preventNewMessageClear: true,
            // messages: [], // Clear messages immediately for cleaner UI
            isLoading: true, // Signal loading state directly
          });

          // Set flags for message loading
          const now = Date.now();
          localStorage.setItem("threadSelectedFromSidebar", localThread.id);
          localStorage.setItem("threadSelectionTimestamp", now.toString());
          localStorage.setItem("lastVisitedThreadId", localThread.id);

          // Wait a moment for state to update
          await new Promise((resolve) => setTimeout(resolve, 10));

          // CRITICAL: Force reload messages for the thread
          // This ensures we get messages even if optimization would skip loading
          try {
            useThreadStore
              .getState()
              .getThreadMessages(localThread.id, { forceRefresh: true });
          } catch (loadError) {
            console.error("Error loading thread messages:", loadError);
          }

          // Navigate to thread after a small delay to allow loading to start
          setTimeout(() => {
            setIsLoading(false);
            navigate(`/chat/${localThread.id}`);
          }, 50);
        } else {
          console.error(
            "[WorkspaceChats] Invalid thread data for selection:",
            localThread
          );
          setIsLoading(false);
        }
      } catch (error) {
        console.error(
          "[WorkspaceChats] Error handling thread selection:",
          error
        );
        setIsLoading(false);
      }
    },
    [navigate]
  );

  // Filter thread details to only include those that belong to the current workspace
  // Use optimistic workspace threads if available, otherwise fall back to workspace prop
  const workspaceThreadIds =
    optimisticWorkspaceThreads || workspace?.workspace_threads || [];

  // Build filtered thread details, using store order if available
  const filteredThreadDetails = (() => {
    // Get thread IDs from the store in order (for reordering support)
    const storeThreadIds = storeThreads.map((t) => t.id);

    // Filter to threads that belong to this workspace
    const filtered = Object.entries(threadDetails)
      .filter(([threadId]) => workspaceThreadIds.includes(threadId))
      .reduce((acc, [threadId, details]) => {
        acc[threadId] = details;
        return acc;
      }, {});

    // If store has threads for this workspace, use store order
    if (storeThreadIds.length > 0) {
      // Sort by store order, with any missing threads at the end
      const sortedEntries = Object.entries(filtered).sort(([idA], [idB]) => {
        const indexA = storeThreadIds.indexOf(idA);
        const indexB = storeThreadIds.indexOf(idB);
        // If both in store, use store order
        if (indexA !== -1 && indexB !== -1) return indexA - indexB;
        // If only A in store, A comes first
        if (indexA !== -1) return -1;
        // If only B in store, B comes first
        if (indexB !== -1) return 1;
        // Neither in store, keep original order
        return 0;
      });
      return Object.fromEntries(sortedEntries);
    }

    return filtered;
  })();

  // Check if the filtered thread details is empty
  const hasNoThreads = Object.keys(filteredThreadDetails).length === 0;

  return (
    <DroppableWorkspace
      workspaceId={workspace?.id}
      className="relative"
      style={{
        // Ensure the container is properly sized
        minHeight: hasNoThreads ? "120px" : "auto",
      }}
    >
      {/* Add CSS for drag and drop styling */}
      <style>{`
        .workspace-drop-zone {
          transition: all 0.2s ease-in-out;
        }

        .workspace-drop-zone.drag-over {
          background-color: rgba(147, 51, 234, 0.1);
          border: 2px dashed rgba(147, 51, 234, 0.4);
          border-radius: 8px;
        }

        .dark .workspace-drop-zone.drag-over {
          background-color: rgba(147, 51, 234, 0.2);
          border-color: rgba(147, 51, 234, 0.6);
        }

        @keyframes slowPulse {
          0%, 100% {
            opacity: 0.6;
          }
          50% {
            opacity: 0.4;
          }
        }
        .skeleton-pulse {
          animation: slowPulse 3s ease-in-out infinite;
        }
      `}</style>

      {loadingThreads ? (
        <div className="relative">
          <div className="space-y-2">
            {/* Create multiple skeleton chat cards */}
            {[1, 2, 3].map((index) => (
              <div
                key={index}
                className="w-full h-16 flex-shrink-0 rounded-lg p-4 bg-transparent flex items-center gap-3 skeleton-pulse"
              >
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-32 bg-gray-200 dark:bg-zinc-800 rounded"></div>
                  <div className="h-3 w-48 bg-gray-200 dark:bg-zinc-800 rounded"></div>
                </div>
                <div className="h-3 w-16 bg-gray-200 dark:bg-zinc-800 rounded"></div>
              </div>
            ))}
          </div>

          {/* Add keyframe animation for the slower pulsing effect */}
          <style>{`
            @keyframes slowPulse {
              0%, 100% {
                opacity: 0.6;
              }
              50% {
                opacity: 0.4;
              }
            }
            .skeleton-pulse {
              animation: slowPulse 3s ease-in-out infinite;
            }
          `}</style>
        </div>
      ) : hasNoThreads ? (
        // Empty state UI matching the image
        <div className="flex flex-col items-center justify-center py-4 px-4">
          <div className="w-12 h-12 mb-2 flex items-center justify-center">
            <svg
              className="w-8 h-8 text-gray-400 dark:text-zinc-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              />
            </svg>
          </div>
          <p className="text-gray-500 dark:text-zinc-400 text-center text-sm">
            Start a new chat, or drag an old one in
          </p>
        </div>
      ) : (
        <div className="relative">
          <div className="space-y-0 overflow-y-visible pr-2 pb-16">
            <SortableContext
              items={Object.values(filteredThreadDetails).map((t) => `project-${t.id}`)}
              strategy={verticalListSortingStrategy}
            >
              {Object.values(filteredThreadDetails).map((thread, index) => (
                <SortableThreadItem
                  key={thread.id}
                  thread={thread}
                  workspaceId={workspace?.id}
                >
                  <ThreadItem
                    thread={thread}
                    index={index}
                    workspace={workspace}
                    firstMessage={threadFirstMessages[thread.id]}
                    isDeleting={deletingThreadIds.has(thread.id)}
                    isRenaming={isRenaming}
                    isSelected={selectedThread?.id === thread.id}
                    openDropdownId={openDropdownId}
                    user={user}
                    newTitle={newTitle}
                    inputRef={inputRef}
                    onThreadSelect={handleLocalThreadSelect}
                    onOpenDropdownChange={setOpenDropdownId}
                    onToggleBookmark={handleToggleBookmark}
                    onStartRenaming={startRenaming}
                    onRename={handleRename}
                    onCancelRename={handleCancelRename}
                    onTitleChange={setNewTitle}
                    onShare={handleShare}
                    onClone={handleThreadClone}
                    onDelete={handleThreadDelete}
                  />
                </SortableThreadItem>
              ))}
            </SortableContext>
          </div>
        </div>
      )}

    </DroppableWorkspace>
  );
}

WorkspaceChats.propTypes = {
  workspace: PropTypes.object,
  threadDetails: PropTypes.object.isRequired,
  loadingThreads: PropTypes.bool.isRequired,
  onCreateChat: PropTypes.func.isRequired,
  creatingChat: PropTypes.bool.isRequired,
  onRefreshThreads: PropTypes.func,
};

export default WorkspaceChats;
